import { useState, useEffect } from 'react';
import { useUserStore, useErrorStore } from '../../../../store/models';
import useModuleErrorDialog from '../../../../store/models/error/useModuleErrorDialog';
import usePhoneVerificationError from './usePhoneVerificationError';

function useTwoStepVerification() {
    const userStore = useUserStore();
    const errorStore = useErrorStore();
    const [userAttributes, setUserAttributes] = useState();
    const [showOtp, setShowOtp] = useState(false);
    const [showOtpSuccess, setShowOtpSuccess] = useState(false);
    const { errors } = usePhoneVerificationError();

    async function fetchUserAttributes() {
        await userStore.getUserDataFromCognito();
    }

    // On mount: fetch attributes only if not already loaded
    useEffect(() => {
        async function init() {
            if (!userStore.user?.userAttributes) {
                await fetchUserAttributes();
            }
        }
        init();
        // eslint-disable-next-line
    }, []);

    // Sync local state when store attributes change
    useEffect(() => {
        if (userStore.user?.userAttributes) {
            setUserAttributes(userStore.user.userAttributes);
        }
        // eslint-disable-next-line
    }, [userStore.observed.userAttributes]);

    // On successful verification: refresh attributes, close OTP dialog, show success dialog
    useEffect(() => {
        if (!userStore.observed.phoneVerificationSuccess) return;
        fetchUserAttributes();
        setShowOtp(false);
        setShowOtpSuccess(true);
        // eslint-disable-next-line
    }, [userStore.observed.phoneVerificationSuccess]);

    /**
     * Cancels an in-progress phone update.
     *
     * Closes the OTP dialog and refreshes attributes from Cognito.
     * - Unverified previous phone: Cognito retains the new unverified number.
     * - Verified previous phone: a Cognito Lambda trigger is responsible for
     *   restoring the original verified number before the user re-authenticates.
     */
    async function cancelPhoneVerification() {
        setShowOtp(false);
        await fetchUserAttributes();
    }

    async function cancelEnterPhoneNumber() {
        await fetchUserAttributes();
    }

    const { renderErrorDialog } = useModuleErrorDialog({
        errors,
    });

    async function sendPhoneNumberVerificationCode({ isResend = false } = {}) {
        errorStore.clearAuthError();
        await userStore.sendPhoneNumberVerificationCode({ isResend });
        const hasError = Boolean(errorStore.authError['sendPhoneNumberVerificationCode']);
        return { hasError };
    }

    async function confirmPhoneNumberVerificationCode({ code }) {
        await userStore.confirmPhoneNumberVerification({ code });
    }

    // Group users don't have phone number field and orgSettings, and the phone number field is hidden for them as well 
    async function updatePhoneNumberInDB({phoneNumber}) {
        const publisher = userStore.publisher
        await userStore.updateProfile({
            user: {
                ...publisher,
                phoneNumber: phoneNumber,
            },
            orgSettings: true,
        }); 
    }

    async function updatePhoneNumber({ phoneNumber }) {
        errorStore.clearAuthError();
        await userStore.updatePhoneNumber({ phoneNumber });
        if (errorStore.authError['updatePhoneNumber']) {
            return { hasError: true };
        }
        await updatePhoneNumberInDB({ phoneNumber });
        await fetchUserAttributes();
        const hasError = Boolean(errorStore.authError['updatePhoneNumber']);
        return { hasError };
    }

    return {
        userAttributes,
        showOtp,
        setShowOtp,
        showOtpSuccess,
        setShowOtpSuccess,
        renderErrorDialog,
        sendPhoneNumberVerificationCode,
        confirmPhoneNumberVerificationCode,
        updatePhoneNumber,
        fetchUserAttributes,
        cancelPhoneVerification,
        cancelEnterPhoneNumber,
    };
}

export default useTwoStepVerification;
